import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Check, Copy, Key, Loader2, Plus, Power, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import {
  useAPIKeys,
  useCreateAPIKey,
  useDeleteAPIKey,
  useUpdateAPIKey,
  type APIKey,
  type CreatedAPIKey,
} from '@/api/integrations'

const schema = z.object({
  name: z.string().trim().min(1, 'name_required'),
  // Lecture seule pour un tableau de bord ou un rapport ; écriture pour appeler, texter,
  // créer des contacts, gérer les employés du widget marque blanche.
  access: z.enum(['read', 'write']),
})
type FormValues = z.infer<typeof schema>

/** Une clé « écrit » si elle porte write ou admin. */
function ecrit(key: APIKey): boolean {
  return key.scopes.includes('write') || key.scopes.includes('admin')
}

/**
 * Clés API : une table dense, la création dans une fenêtre, la clé et le secret
 * révélés une seule fois. Le serveur applique déjà les droits ; ici on les choisit
 * et on les montre.
 */
export function APIKeysTab() {
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const dateLocale = locale === 'fr' ? 'fr-CA' : 'en-CA'

  const { data: keys = [], isLoading } = useAPIKeys()
  const createKey = useCreateAPIKey()
  const updateKey = useUpdateAPIKey()
  const deleteKey = useDeleteAPIKey()

  const [showForm, setShowForm] = useState(false)
  const [revealedKey, setRevealedKey] = useState<CreatedAPIKey | null>(null)
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const [pendingDelete, setPendingDelete] = useState<APIKey | null>(null)

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', access: 'write' },
  })

  const fermerFormulaire = () => {
    setShowForm(false)
    form.reset({ name: '', access: 'write' })
  }

  const handleCreate = async (values: FormValues) => {
    try {
      const created = await createKey.mutateAsync({
        name: values.name,
        scopes: values.access === 'write' ? ['read', 'write'] : ['read'],
      })
      fermerFormulaire()
      setRevealedKey(created)
    } catch {
      toast.error(t('manager.integrations.keys.toast.createError'))
    }
  }

  const handleToggle = async (key: APIKey) => {
    try {
      await updateKey.mutateAsync({ id: key.id, patch: { is_active: !key.is_active } })
    } catch {
      toast.error(t('manager.integrations.keys.toast.toggleError'))
    }
  }

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return
    try {
      await deleteKey.mutateAsync(pendingDelete.id)
      toast.success(t('manager.integrations.keys.toast.deleted'))
    } catch {
      toast.error(t('manager.integrations.keys.toast.deleteError'))
    } finally {
      setPendingDelete(null)
    }
  }

  const copy = async (value: string, field: string) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopiedField(field)
      setTimeout(() => setCopiedField(null), 2000)
    } catch {
      toast.error(t('manager.integrations.keys.toast.copyError'))
    }
  }

  const formatDate = (iso: string | null) => {
    if (!iso) return null
    return new Date(iso).toLocaleDateString(dateLocale, { year: 'numeric', month: 'short', day: 'numeric' })
  }

  const access = form.watch('access')

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            {t('manager.integrations.keys.title')}
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {t('manager.integrations.keys.description')}
          </p>
        </div>
        <Button type="button" onClick={() => setShowForm(true)} className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="w-4 h-4 mr-2" />
          {t('manager.integrations.keys.create')}
        </Button>
      </div>

      {/* Création */}
      <Dialog open={showForm} onOpenChange={(o) => !o && fermerFormulaire()}>
        <DialogContent className="sm:max-w-lg">
          <form onSubmit={form.handleSubmit(handleCreate)}>
            <DialogHeader>
              <DialogTitle>{t('manager.integrations.keys.create')}</DialogTitle>
              <DialogDescription>{t('manager.integrations.keys.createHelp')}</DialogDescription>
            </DialogHeader>

            <div className="space-y-5 py-4">
              <div>
                <Label htmlFor="key-name" className="mb-2 block">
                  {t('manager.integrations.keys.nameLabel')}
                </Label>
                <Input
                  id="key-name"
                  autoFocus
                  placeholder={t('manager.integrations.keys.namePlaceholder')}
                  {...form.register('name')}
                />
                {form.formState.errors.name && (
                  <p className="text-xs text-red-500 mt-1">
                    {t(`manager.integrations.errors.${form.formState.errors.name.message}`)}
                  </p>
                )}
              </div>

              <div>
                <Label className="mb-2 block">{t('manager.integrations.keys.accessLabel')}</Label>
                <div className="grid gap-2">
                  {(['read', 'write'] as const).map((niveau) => (
                    <label
                      key={niveau}
                      className={cn(
                        'flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors',
                        access === niveau
                          ? 'border-blue-500 bg-blue-50/60 dark:bg-blue-500/10'
                          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300',
                      )}
                    >
                      <input type="radio" value={niveau} className="mt-1" {...form.register('access')} />
                      <span>
                        <span className="block text-sm font-medium text-slate-900 dark:text-white">
                          {t(`manager.integrations.keys.access.${niveau}`)}
                        </span>
                        <span className="block text-xs text-slate-500 dark:text-slate-400">
                          {t(`manager.integrations.keys.access.${niveau}Help`)}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={fermerFormulaire}>
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={createKey.isPending || !form.formState.isValid}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {createKey.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {t('manager.integrations.keys.generate')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Clé et secret, une seule fois */}
      <Dialog open={!!revealedKey} onOpenChange={(o) => !o && setRevealedKey(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('manager.integrations.keys.createdTitle')}</DialogTitle>
            <DialogDescription>{t('manager.integrations.keys.createdWarning')}</DialogDescription>
          </DialogHeader>
          {revealedKey && (
            <div className="space-y-4 py-2">
              <RevealField
                label={t('manager.integrations.keys.keyLabel')}
                value={revealedKey.key}
                isCopied={copiedField === 'key'}
                onCopy={() => void copy(revealedKey.key, 'key')}
              />
              <RevealField
                label={t('manager.integrations.keys.secretLabel')}
                value={revealedKey.secret}
                isCopied={copiedField === 'secret'}
                onCopy={() => void copy(revealedKey.secret, 'secret')}
              />
            </div>
          )}
          <DialogFooter>
            <Button type="button" onClick={() => setRevealedKey(null)}>
              {t('manager.integrations.keys.done')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Liste */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : keys.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 dark:border-slate-700 py-14 text-center">
          <Key className="w-8 h-8 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
          <p className="text-sm text-slate-500 dark:text-slate-400">{t('manager.integrations.keys.empty')}</p>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden bg-white dark:bg-slate-900">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>{t('manager.integrations.keys.columns.name')}</TableHead>
                <TableHead className="hidden md:table-cell">{t('manager.integrations.keys.columns.key')}</TableHead>
                <TableHead>{t('manager.integrations.keys.columns.access')}</TableHead>
                <TableHead className="hidden lg:table-cell">{t('manager.integrations.keys.lastUsed')}</TableHead>
                <TableHead className="hidden lg:table-cell">{t('manager.integrations.keys.created')}</TableHead>
                <TableHead>{t('manager.integrations.keys.columns.status')}</TableHead>
                <TableHead className="w-[88px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {keys.map((key) => (
                <TableRow key={key.id} className={cn(!key.is_active && 'opacity-60')}>
                  <TableCell className="font-medium text-slate-900 dark:text-white">{key.name}</TableCell>
                  <TableCell className="hidden md:table-cell font-mono text-xs text-slate-500">
                    {key.key_prefix}••••••••
                  </TableCell>
                  <TableCell>
                    <span
                      className={cn(
                        'px-2 py-0.5 rounded-md text-[11px] font-semibold',
                        ecrit(key)
                          ? 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300'
                          : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
                      )}
                    >
                      {t(ecrit(key) ? 'manager.integrations.keys.access.writeBadge' : 'manager.integrations.keys.access.readBadge')}
                    </span>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-sm text-slate-500">
                    {formatDate(key.last_used_at) ?? (
                      <span className="text-slate-400">{t('manager.integrations.keys.neverUsed')}</span>
                    )}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-sm text-slate-500">{formatDate(key.created_at)}</TableCell>
                  <TableCell>
                    <span
                      className={cn(
                        'inline-flex items-center gap-1.5 text-xs font-medium',
                        key.is_active ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-500',
                      )}
                    >
                      <span className={cn('w-1.5 h-1.5 rounded-full', key.is_active ? 'bg-emerald-500' : 'bg-slate-400')} />
                      {key.is_active ? t('manager.integrations.keys.active') : t('manager.integrations.keys.inactive')}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => void handleToggle(key)}
                        disabled={updateKey.isPending}
                        className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-500/10 rounded-lg transition-colors"
                        title={key.is_active ? t('manager.integrations.keys.deactivate') : t('manager.integrations.keys.activate')}
                      >
                        <Power className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setPendingDelete(key)}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors"
                        title={t('manager.integrations.keys.delete')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('manager.integrations.keys.deleteDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('manager.integrations.keys.deleteDialog.description', { name: pendingDelete?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteKey.isPending}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                void handleConfirmDelete()
              }}
              disabled={deleteKey.isPending}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {t('manager.integrations.keys.deleteDialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function RevealField({
  label,
  value,
  isCopied,
  onCopy,
}: {
  label: string
  value: string
  isCopied: boolean
  onCopy: () => void
}) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">{label}</label>
      <div className="flex items-center gap-2 mt-1">
        {/* Never filmed by session replay nor shown during a screen share. */}
        <code
          data-masque
          className="ph-no-capture flex-1 bg-slate-50 dark:bg-slate-800 px-3 py-2 rounded-lg text-sm font-mono border border-slate-200 dark:border-slate-700 break-all"
        >
          {value}
        </code>
        <Button type="button" variant="outline" size="sm" onClick={onCopy} className="shrink-0">
          {isCopied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  )
}
